import { useEffect, useState } from "react";

import { Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
} from "../components/BreadcrumbDropdownMenu";
import { buildPathLinks } from "../components/breadcrumbPath";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";
import { useDataGridStore } from "../store/DataGrid";

import type { AeronetViewProps } from "../types";

// Exported so App.tsx's nav-children registry (src/app/navModeRegistry.ts)
// can derive Stores > Inventory's dropdown children from THIS list instead of
// a hand-typed copy — the hand-typed copy is what let it drift out of sync
// (missing "Transfers") in the first place. See WP3 recon.
export const breadcrumbMenu: {
  [key: string]: { label: string; description: string };
} = {
  "/inventory/parts": { label: "Parts", description: "Master part records" },
  "/inventory/min-max": { label: "Min - Max", description: "Reorder levels" },
  "/inventory/demand": {
    label: "Demand",
    description: "Outstanding requirements",
  },
  "/inventory/rfq": { label: "RFQ", description: "Request for Quote" },
  "/inventory/ordered": { label: "Ordered", description: "Parts Ordered" },
  "/inventory/stock": { label: "Stock", description: "On-hand by bin" },
  "/inventory/transfer": {
    label: "Transfers",
    description: "Between locations",
  },
  "/inventory/issued": { label: "Issued", description: "Issued Parts" },
  "/inventory/sold": { label: "Sold", description: "Parts Sold" },
};

const allRoutes: { [key: string]: string } = {
  "/inventory": "Inventory",
  ...Object.fromEntries(
    Object.entries(breadcrumbMenu).map(([path, item]) => [path, item.label])
  ),
};

interface InventoryLayoutProps extends AeronetViewProps {
  originalUrl: string;
  canCreatePart: boolean;
}

const InventoryLayout: React.FC<InventoryLayoutProps> = () => {
  const location = useLocation();
  const resetQuickFilter = useDataGridStore((s) => s.resetQuickFilter);
  const popupState = usePopupState({
    variant: "popover",
    popupId: "breadcrumbMenu",
  });

  const [pathLinks, setPathLinks] = useState<string[]>([]);

  useEffect(() => {
    setPathLinks(buildPathLinks(location.pathname));
  }, [location.pathname]);

  return (
    <>
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          {pathLinks.map((path) => {
            if (path === location.pathname) {
              return (
                <BreadcrumbHoverRoot key={path} popupState={popupState}>
                  <Button
                    endIcon={<BreadcrumbChevronIcon />}
                    sx={breadcrumbCurrentTriggerSx}
                  >
                    <Typography
                      variant="h2"
                      component="span"
                      sx={breadcrumbCurrentLabelSx}
                    >
                      {allRoutes[location.pathname]}
                    </Typography>
                  </Button>
                  <BreadcrumbDropdownMenu
                    popupState={popupState}
                    variant="inventory"
                  >
                    {Object.entries(breadcrumbMenu).map(([key, item]) => (
                      <BreadcrumbMenuLinkItem
                        key={key.split("/")[2]}
                        to={key}
                        label={item.label}
                        description={item.description}
                        selected={location.pathname === key}
                        end={key === "/inventory"}
                        onClick={() => {
                          resetQuickFilter();
                          popupState.close();
                        }}
                      />
                    ))}
                  </BreadcrumbDropdownMenu>
                </BreadcrumbHoverRoot>
              );
            }
            return (
              <LinkRouter to={path} key={path}>
                {allRoutes[path]} /
              </LinkRouter>
            );
          })}
        </Title>
      </BreadcrumbHeaderListing>
      <Outlet />
    </>
  );
};

export default InventoryLayout;
